import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, Heart, User, Search, ChevronDown, ChevronRight, Tag, Ruler, Package, LogOut, UserPlus, Volume2, VolumeX, Play, Pause, SkipForward, Music, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CartSheet from './CartSheet';

/* ── Your Curated Playlists ── */
const TRACKS = {
  hiphop: [
    { id: 'vk6014HuxcE', title: 'Empire State of Mind', artist: 'Jay-Z ft. Alicia Keys' },
    { id: 'xpVfcZ0ZcFM', title: "God's Plan", artist: 'Drake' },
    { id: 'uxpDa-c-4Mc', title: 'Hotline Bling', artist: 'Drake' },
    { id: 'MXaqpJmX7JE', title: 'HUMBLE.', artist: 'Kendrick Lamar' },
    { id: '1Vf4mMCpNY0', title: 'A Milli', artist: 'Lil Wayne' },
    { id: '6ONRf7h3Mdk', title: 'SICKO MODE', artist: 'Travis Scott ft. Drake' },
    { id: 'RubBzkZzpUA', title: 'Started From The Bottom', artist: 'Drake' },
  ],
  reggae: [
    { id: 'HNBCVM4KbUM', title: 'Three Little Birds', artist: 'Bob Marley' },
    { id: 'dW2MmuA1nI4', title: 'Temperature', artist: 'Sean Paul' },
    { id: '6W5pq4bIzIw', title: 'Boombastic', artist: 'Shaggy' },
    { id: 'DWHTrh50Tfg', title: 'Fever', artist: 'Vybz Kartel' },
    { id: 'Kb-0Eo0Yk0o', title: 'Untold Stories', artist: 'Buju Banton' },
    { id: '_GZlJGERbvE', title: 'Welcome to Jamrock', artist: 'Damian Marley' },
    { id: 'WI-xQ7IksUc', title: 'Who Am I', artist: 'Beenie Man' },
    { id: 'tMPP1F45ptM', title: 'Heads High', artist: 'Mr. Vegas' },
    { id: 'xG7VLUV3opk', title: 'Murder She Wrote', artist: 'Chaka Demus & Pliers' },
  ],
  afrobeat: [
    { id: '421w1j87fEM', title: 'Last Last', artist: 'Burna Boy' },
    { id: 'jipQpjUA_o8', title: 'Essence', artist: 'Wizkid ft. Tems' },
    { id: '3Iyuym-Gci0', title: 'Fall', artist: 'Davido' },
    { id: 'CQLsdm1ZYAw', title: 'Calm Down', artist: 'Rema' },
    { id: 'RY3B_XXmTYU', title: 'Love Nwantiti', artist: 'CKay' },
    { id: 'VDcEJE633rM', title: 'Free Mind', artist: 'Tems' },
    { id: 'Ecl8Aod0Tl0', title: 'On The Low', artist: 'Burna Boy' },
    { id: 'Q7QiLceJSLQ', title: 'Ojuelegba', artist: 'Wizkid' },
    { id: 'T4G5uTd72EM', title: 'Lonely at the Top', artist: 'Asake' },
  ],
};

const GENRES = [
  { key: 'mix', name: 'Mix', color: '#E8D5F5' },
  { key: 'hiphop', name: 'Hip-Hop', color: '#FAD4D4' },
  { key: 'reggae', name: 'Reggae', color: '#D5FDD8' },
  { key: 'afrobeat', name: 'Afrobeat', color: '#FDF0D5' },
];

/* ── Helpers ── */
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const buildPlaylist = (genreKey) => {
  if (genreKey === 'mix') {
    const hh = shuffle([...TRACKS.hiphop]);
    const reg = shuffle([...TRACKS.reggae]);
    const afro = shuffle([...TRACKS.afrobeat]);
    const maxLen = Math.max(hh.length, reg.length, afro.length);
    const mixed = [];
    for (let i = 0; i < maxLen; i++) {
      if (i < hh.length) mixed.push(hh[i]);
      if (i < reg.length) mixed.push(reg[i]);
      if (i < afro.length) mixed.push(afro[i]);
    }
    return mixed;
  }
  return shuffle([...TRACKS[genreKey]]);
};

/* ── Navigation Config ── */
const navGroups = [
  {
    label: 'Shop',
    items: [
      { name: 'Men', path: '/products?category=men' },
      { name: 'Women', path: '/products?category=women' },
      { name: 'Boys', path: '/products?category=boys' },
      { name: 'Girls', path: '/products?category=girls' },
      { name: 'Babies', path: '/products?category=babies' },
      { name: 'All Products', path: '/products' },
    ],
  },
  {
    label: 'Footwear',
    items: [
      { name: 'All Footwear', path: '/footwear' },
      { name: 'Timberland', path: '/products?category=footwear&brand=timberland' },
      { name: 'Nike & Jordan', path: '/products?category=footwear&brand=nike' },
    ],
  },
  // Accessories submenu SUSPENDED 2026-05 — restore by uncommenting and merging Hats & Caps back in.
  // {
  //   label: 'Accessories',
  //   items: [
  //     { name: 'Hats & Caps', path: '/products?category=hats' },
  //     { name: 'Fragrances', path: '/products?category=accessories' },
  //   ],
  // },
  {
    label: 'Hats',
    items: [
      { name: 'All Hats & Caps', path: '/products?category=hats' },
    ],
  },
  {
    label: 'More',
    items: [
      { name: 'Sale Items', path: '/products?promo=true', icon: Tag },
      { name: 'Changing Room', path: '/changing-room', icon: Ruler },
      { name: 'Size Guide', path: '/size-guide', icon: Ruler },
    ],
  },
];

function DesktopDropdown({ group }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const handleEnter = () => { clearTimeout(timeoutRef.current); setOpen(true); };
  const handleLeave = () => { timeoutRef.current = setTimeout(() => setOpen(false), 150); };
  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button data-testid={`nav-group-${group.label.toLowerCase()}`} className="flex items-center gap-1 text-sm font-semibold uppercase tracking-wider hover:text-[#525252] transition-colors py-2">
        {group.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-[#FDFBF7] border-2 border-[#0A0A0A] shadow-[4px_4px_0px_#0A0A0A] z-50">
          {group.items.map((item) => (
            <Link key={item.name} to={item.path} data-testid={`dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium hover:bg-[#FAD4D4] transition-colors border-b border-[#E5E5E5] last:border-b-0" onClick={() => setOpen(false)}>
              {item.icon && <item.icon className="w-4 h-4" />}{item.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileAccordion({ group, onClose }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E5E5E5]">
      <button data-testid={`mobile-group-${group.label.toLowerCase()}`} className="w-full flex items-center justify-between px-6 py-5 text-base font-bold uppercase tracking-wider min-h-[52px]" onClick={() => setOpen(!open)}>
        {group.label} <ChevronRight className={`w-5 h-5 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="bg-[#F5F3EF] pt-1 pb-4 border-t border-[#E5E5E5]">
          {group.items.map((item) => (
            <Link key={item.name} to={item.path} data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex items-center gap-3 px-8 py-4 text-sm font-medium text-[#525252] hover:text-[#0A0A0A] hover:bg-[#FAD4D4] transition-colors min-h-[48px]" onClick={onClose}>
              {item.icon && <item.icon className="w-4 h-4" />}{item.name}
            </Link>
          ))}
          {/* Spacer to guarantee no overlap with the next accordion group's touch target */}
          <div className="h-2" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

/* ── Main Header ── */
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [radioOpen, setRadioOpen] = useState(false);
  const [radioPlaying, setRadioPlaying] = useState(false);
  const [activeGenre, setActiveGenre] = useState('mix');
  const [currentTrack, setCurrentTrack] = useState(null);
  const [volume, setVolume] = useState(70);
  const [muted, setMuted] = useState(false);

  const playerRef = useRef(null);
  const playlistRef = useRef([]);
  const trackIndexRef = useRef(0);
  const ytReadyRef = useRef(false);
  const containerRef = useRef(null);
  const skipCountRef = useRef(0);

  const { user, logout } = useAuth();
  const { cartCount, cartOpen, setCartOpen } = useCart();
  const navigate = useNavigate();

  /* ── Load YT API ── */
  useEffect(() => {
    if (window.YT && window.YT.Player) { ytReadyRef.current = true; return; }
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { ytReadyRef.current = true; if (prev) prev(); };
  }, []);

  /* ── Skip track ── */
  const skipTrack = useCallback(() => {
    const pl = playlistRef.current;
    if (!pl.length) return;
    skipCountRef.current++;
    if (skipCountRef.current > pl.length) { skipCountRef.current = 0; setRadioPlaying(false); return; }
    trackIndexRef.current = (trackIndexRef.current + 1) % pl.length;
    const next = pl[trackIndexRef.current];
    setCurrentTrack(next);
    if (playerRef.current?.loadVideoById) playerRef.current.loadVideoById(next.id);
  }, []);

  /* ── Create player (completely hidden, no clickable elements) ── */
  const initPlayer = useCallback((videoId) => {
    if (!window.YT?.Player) return;
    if (playerRef.current) { try { playerRef.current.destroy(); } catch (_) {} playerRef.current = null; }
    const container = containerRef.current;
    if (!container) return;
    const div = document.createElement('div');
    div.id = 'yt-radio-hidden';
    container.innerHTML = '';
    container.appendChild(div);

    playerRef.current = new window.YT.Player('yt-radio-hidden', {
      height: '1', width: '1', videoId,
      playerVars: { autoplay: 1, controls: 0, disablekb: 1, fs: 0, iv_load_policy: 3, modestbranding: 1, playsinline: 1, rel: 0, origin: window.location.origin },
      events: {
        onReady: (e) => {
          // Block clicks on the iframe so it never navigates away
          const iframe = container.querySelector('iframe');
          if (iframe) { iframe.style.pointerEvents = 'none'; iframe.setAttribute('tabindex', '-1'); }
          e.target.unMute();
          e.target.setVolume(muted ? 0 : volume);
          setRadioPlaying(true);
          skipCountRef.current = 0;
        },
        onStateChange: (e) => {
          if (e.data === window.YT.PlayerState.ENDED) { skipCountRef.current = 0; skipTrack(); }
          else if (e.data === window.YT.PlayerState.PLAYING) { setRadioPlaying(true); skipCountRef.current = 0; }
        },
        onError: () => skipTrack(),
      },
    });
  }, [volume, muted, skipTrack]);

  /* ── Volume sync ── */
  useEffect(() => {
    if (!playerRef.current?.setVolume) return;
    if (muted) playerRef.current.mute();
    else { playerRef.current.unMute(); playerRef.current.setVolume(volume); }
  }, [volume, muted]);

  /* ── Start radio ── */
  const startRadio = useCallback((genreKey = 'mix') => {
    const pl = buildPlaylist(genreKey);
    playlistRef.current = pl;
    trackIndexRef.current = 0;
    setActiveGenre(genreKey);
    setCurrentTrack(pl[0]);
    skipCountRef.current = 0;
    const tryInit = () => { if (window.YT?.Player) initPlayer(pl[0].id); else setTimeout(tryInit, 300); };
    tryInit();
  }, [initPlayer]);

  const handleGenreSelect = (genreKey) => {
    if (activeGenre === genreKey && radioPlaying) { playerRef.current?.pauseVideo(); setRadioPlaying(false); return; }
    startRadio(genreKey);
  };

  const toggleRadio = () => {
    if (!playerRef.current) { startRadio(activeGenre); return; }
    if (radioPlaying) { playerRef.current.pauseVideo(); setRadioPlaying(false); }
    else { playerRef.current.playVideo(); setRadioPlaying(true); }
  };

  const handleSkip = () => { skipCountRef.current = 0; skipTrack(); };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) { navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`); setSearchQuery(''); setSearchOpen(false); setMobileMenuOpen(false); }
  };

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <>
    {/* YouTube player — completely hidden, pointer-events blocked so it can NEVER be clicked */}
    <div ref={containerRef} style={{ position: 'fixed', width: '1px', height: '1px', overflow: 'hidden', opacity: 0, pointerEvents: 'none', zIndex: -1, top: '-9999px', left: '-9999px' }} />

    <header className="sticky top-0 z-50 border-b-2 border-[#0A0A0A] bg-[#FDFBF7]/95 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button data-testid="mobile-menu-btn" className="md:hidden p-2" onClick={() => setMobileMenuOpen(true)}><Menu className="w-6 h-6" /></button>

          <Link to="/" className="flex items-center" data-testid="logo-link">
            <h1 className="font-black text-xl md:text-2xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>DOE WIZ</h1>
          </Link>

          <nav className="hidden md:flex items-center space-x-6">
            {navGroups.map((group) => <DesktopDropdown key={group.label} group={group} />)}
          </nav>

          <div className="flex items-center space-x-1 md:space-x-3">
            {/* Play dropdown */}
            <div className="relative">
              <button data-testid="play-btn" className={`flex items-center gap-1.5 px-2 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors rounded-sm ${radioPlaying ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#FAD4D4]'}`} onClick={() => setRadioOpen(!radioOpen)}>
                {radioPlaying ? (
                  <div className="flex gap-0.5 items-end h-3">
                    <span className="w-0.5 rounded-full animate-pulse" style={{ height: '5px', animationDelay: '0ms', backgroundColor: '#FAD4D4' }} />
                    <span className="w-0.5 rounded-full animate-pulse" style={{ height: '10px', animationDelay: '150ms', backgroundColor: '#FAD4D4' }} />
                    <span className="w-0.5 rounded-full animate-pulse" style={{ height: '6px', animationDelay: '300ms', backgroundColor: '#FAD4D4' }} />
                    <span className="w-0.5 rounded-full animate-pulse" style={{ height: '8px', animationDelay: '100ms', backgroundColor: '#FAD4D4' }} />
                  </div>
                ) : (<Play className="w-4 h-4" />)}
                <span className="hidden sm:inline">Play</span>
              </button>

              {radioOpen && (
                <div className="fixed sm:absolute top-14 sm:top-full right-2 sm:right-0 mt-0 sm:mt-1 w-[calc(100vw-1rem)] sm:w-64 bg-[#0A0A0A] border-2 border-[#333] shadow-[4px_4px_0px_#333] z-50" data-testid="play-dropdown">
                  <div className="px-3 py-2 border-b border-[#333] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Music className="w-3.5 h-3.5 text-[#FAD4D4]" />
                      <span className="text-white text-xs font-bold uppercase tracking-wider">Doe Wiz Radio</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {radioPlaying && <button onClick={handleSkip} data-testid="skip-btn" className="text-white/60 hover:text-white transition-colors"><SkipForward className="w-3.5 h-3.5" /></button>}
                      <button onClick={toggleRadio} data-testid="play-pause-btn" className="text-white/60 hover:text-white transition-colors">
                        {radioPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  {currentTrack && radioPlaying && (
                    <div className="px-3 py-2 border-b border-[#333] bg-[#111]">
                      <div className="flex items-center gap-2">
                        <Music className="w-3 h-3 text-[#FAD4D4] flex-shrink-0" />
                        <div className="overflow-hidden">
                          <p className="text-white text-xs font-semibold truncate" data-testid="now-playing-title">{currentTrack.title}</p>
                          <p className="text-white/50 text-[10px] truncate" data-testid="now-playing-artist">{currentTrack.artist}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-2 space-y-1">
                    {GENRES.map((genre) => (
                      <button key={genre.key} data-testid={`genre-${genre.key}`} onClick={() => handleGenreSelect(genre.key)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-sm transition-all ${activeGenre === genre.key && radioPlaying ? 'text-[#0A0A0A]' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
                        style={activeGenre === genre.key && radioPlaying ? { backgroundColor: genre.color } : {}}>
                        {activeGenre === genre.key && radioPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                        {genre.name}
                        {genre.key === 'mix' && <span className="text-[10px] ml-auto opacity-60">All Genres</span>}
                      </button>
                    ))}
                  </div>

                  <div className="px-3 pb-2 flex items-center gap-2">
                    <button onClick={() => setMuted(!muted)} className="text-white/60 hover:text-white" data-testid="mute-btn">
                      {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                    </button>
                    <input type="range" min="0" max="100" step="5" value={muted ? 0 : volume}
                      onChange={(e) => { setVolume(parseInt(e.target.value)); setMuted(false); }}
                      className="flex-1 h-1 accent-[#FAD4D4] cursor-pointer" data-testid="volume-slider" />
                  </div>
                </div>
              )}
            </div>

            <button data-testid="search-btn" className="p-2 hover:bg-[#FAD4D4] transition-colors" onClick={() => setSearchOpen(!searchOpen)}><Search className="w-5 h-5" /></button>
            <Link to={user ? '/wishlist' : '/login'} data-testid="wishlist-btn" className="p-2 hover:bg-[#FAD4D4] transition-colors"><Heart className="w-5 h-5" /></Link>

            {user ? (
              <div className="relative group">
                <button data-testid="account-btn" className="p-2 hover:bg-[#FAD4D4] transition-colors"><User className="w-5 h-5" /></button>
                <div className="absolute right-0 mt-2 w-48 bg-[#FDFBF7] border-2 border-[#0A0A0A] shadow-[4px_4px_0px_#0A0A0A] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="p-3 border-b-2 border-[#0A0A0A]"><p className="font-semibold truncate">{user.name}</p><p className="text-sm text-[#525252] truncate">{user.email}</p></div>
                  <Link to="/orders" data-testid="orders-link" className="flex items-center gap-2 px-4 py-2.5 hover:bg-[#FAD4D4] transition-colors text-sm"><Package className="w-4 h-4" /> My Orders</Link>
                  <Link to="/wishlist" data-testid="wishlist-link" className="flex items-center gap-2 px-4 py-2.5 hover:bg-[#FAD4D4] transition-colors text-sm"><Heart className="w-4 h-4" /> Wishlist</Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" data-testid="admin-link" className="flex items-center gap-2 px-4 py-2.5 hover:bg-[#FAD4D4] transition-colors text-sm border-t border-[#0A0A0A]/10 font-bold uppercase tracking-wider text-xs">
                      <BarChart3 className="w-4 h-4" /> Admin Dashboard
                    </Link>
                  )}
                  <button data-testid="logout-btn" onClick={logout} className="w-full flex items-center gap-2 text-left px-4 py-2.5 hover:bg-[#FAD4D4] transition-colors text-sm text-red-600"><LogOut className="w-4 h-4" /> Logout</button>
                </div>
              </div>
            ) : (
              <Link to="/login" data-testid="login-link" className="p-2 hover:bg-[#FAD4D4] transition-colors"><User className="w-5 h-5" /></Link>
            )}

            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <button data-testid="cart-btn" className="p-2 hover:bg-[#FAD4D4] transition-colors relative">
                  <ShoppingBag className="w-5 h-5" />
                  {cartCount > 0 && <span className="cart-badge" data-testid="cart-count">{cartCount}</span>}
                </button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md border-l-2 border-[#0A0A0A] p-0"><CartSheet /></SheetContent>
            </Sheet>
          </div>
        </div>

        {searchOpen && (
          <div className="pb-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input type="text" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 border-2 border-[#0A0A0A] rounded-none focus:ring-0 focus:bg-[#FDF0D5]" data-testid="search-input" autoFocus />
              <Button type="submit" data-testid="search-submit" className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none hover:bg-[#0A0A0A]/90">Search</Button>
            </form>
          </div>
        )}
      </div>
    </header>

    {mobileMenuOpen && (
      <div className="fixed inset-0 z-[9999] md:hidden">
        <div className="absolute inset-0 bg-black/50" onClick={closeMobile} />
        <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-[320px] bg-[#FDFBF7] overflow-y-auto shadow-2xl">
          <div className="flex items-center justify-between px-6 py-5 border-b-2 border-[#0A0A0A]">
            <h2 className="font-black text-xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>DOE WIZ</h2>
            <button data-testid="mobile-menu-close" onClick={closeMobile} className="p-1"><X className="w-6 h-6" /></button>
          </div>
          <div className="px-6 py-4 border-b border-[#E5E5E5]">
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="flex-1 border-2 border-[#0A0A0A] rounded-none text-sm h-9" data-testid="mobile-search-input" />
              <Button type="submit" className="bg-[#0A0A0A] text-white rounded-none h-9 px-3 text-sm"><Search className="w-4 h-4" /></Button>
            </form>
          </div>
          {navGroups.map((group) => <MobileAccordion key={group.label} group={group} onClose={closeMobile} />)}
          <div className="border-b border-[#E5E5E5]">
            <div className="px-6 py-3"><span className="text-xs font-bold uppercase tracking-wider text-[#999]">Account</span></div>
            {user ? (<>
              <div className="px-6 py-2 bg-[#F5F3EF]"><p className="font-semibold text-sm truncate">{user.name}</p><p className="text-xs text-[#525252] truncate">{user.email}</p></div>
              <Link to="/orders" onClick={closeMobile} className="flex items-center gap-3 px-6 py-3 text-sm font-medium hover:bg-[#FAD4D4] transition-colors"><Package className="w-4 h-4" /> My Orders</Link>
              <Link to="/wishlist" onClick={closeMobile} className="flex items-center gap-3 px-6 py-3 text-sm font-medium hover:bg-[#FAD4D4] transition-colors"><Heart className="w-4 h-4" /> Wishlist</Link>
              <button onClick={() => { logout(); closeMobile(); }} className="flex items-center gap-3 w-full px-6 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"><LogOut className="w-4 h-4" /> Logout</button>
            </>) : (<>
              <Link to="/login" onClick={closeMobile} className="flex items-center gap-3 px-6 py-3 text-sm font-medium hover:bg-[#FAD4D4] transition-colors"><User className="w-4 h-4" /> Login</Link>
              <Link to="/register" onClick={closeMobile} className="flex items-center gap-3 px-6 py-3 text-sm font-medium hover:bg-[#FAD4D4] transition-colors"><UserPlus className="w-4 h-4" /> Register</Link>
            </>)}
          </div>
          <div className="px-6 py-5">
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/doewizapparel" target="_blank" rel="noopener noreferrer" className="text-[#525252] hover:text-[#0A0A0A]"><svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg></a>
              <a href="https://www.tiktok.com/@doewiz" target="_blank" rel="noopener noreferrer" className="text-[#525252] hover:text-[#0A0A0A]"><svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.87a8.28 8.28 0 004.76 1.5V6.93a4.84 4.84 0 01-1-.24z"/></svg></a>
              <a href="https://www.facebook.com/doewizfanpage" target="_blank" rel="noopener noreferrer" className="text-[#525252] hover:text-[#0A0A0A]"><svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg></a>
              <a href="https://x.com/DoeWizApparel" target="_blank" rel="noopener noreferrer" className="text-[#525252] hover:text-[#0A0A0A]"><svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></a>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
